/**
 * Shared app state.
 *
 * Kept in its own module so screens can import it without importing the
 * router that imports them.
 */

import type { Puzzle } from '../core/generator';
import type { Session } from '../game/session';
import type { DayResult, Stats } from '../state/store';

export type Screen = 'home' | 'play' | 'reveal' | 'archive';

export interface AppCtx {
  screen: Screen;
  /** The day currently open. */
  index: number;
  /** `daily` is today; `archive` is a past day being replayed for fun. */
  mode: 'daily' | 'archive';
  puzzle: Puzzle | null;
  session: Session | null;
  startedAt: number;
  stats: Stats;
  /** The result for `index`, or null if it has not been finished. */
  result: DayResult | null;
  /** Colours the player has made this session, for the share card. */
  trail: string[];
  go(screen: Screen): void;
  /** Load a day and drop into play. */
  start(index: number, mode: 'daily' | 'archive'): void;
  /** Grade, persist and show the reveal. */
  finish(): void;
  /** Re-read persisted stats, then re-render the current screen. */
  refresh(): void;
}

function notReady(): never {
  throw new Error('ctx used before app boot');
}

/**
 * Teardown callbacks for the current screen. The router runs them all before
 * mounting the next one, so a screen can subscribe to a session without
 * leaking a listener every time the player navigates.
 */
const disposers: Array<() => void> = [];

export function onLeave(fn: () => void): void {
  disposers.push(fn);
}

export function disposeScreen(): void {
  while (disposers.length) disposers.pop()?.();
}

export const ctx: AppCtx = {
  screen: 'home',
  index: 0,
  mode: 'daily',
  puzzle: null,
  session: null,
  startedAt: 0,
  stats: null as unknown as Stats,
  result: null,
  trail: [],
  go: notReady,
  start: notReady,
  finish: notReady,
  refresh: notReady,
};
